import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  App, Button, Col, InputNumber, Row, Select, Table, Typography,
} from 'antd';
import FormLayout from 'components/FormLayout';
import { AdminContext } from 'constants/contexts';
import useFetchedData from 'hooks/useFetchedData';
import React, { useContext, useMemo, useState } from 'react';

function normalizeIngredientsResponse(data = {}) {
  const list = (
    data?.data?.results
    || data?.results
    || data?.data
    || data
  );
  const normalizedList = Array.isArray(list) ? list : [];
  return normalizedList.map((ingredient) => ({
    ...ingredient,
    value: ingredient.id,
    label: ingredient.name,
  }));
}

function validateNegativeModifierQuantity(ingredientId, quantity, itemRecipeLines) {
  const qty = Number(quantity);
  if (Number.isNaN(qty) || qty >= 0) {
    return { valid: true };
  }
  const itemLine = (itemRecipeLines || []).find((line) => line.ingredient === ingredientId);
  const label = itemLine?.ingredient_name || 'This ingredient';
  if (!itemLine || Number(itemLine.quantity) <= 0) {
    return {
      valid: false,
      error: `${label} must be on the item recipe before using a negative modifier quantity.`,
    };
  }
  if (Math.abs(qty) > Number(itemLine.quantity)) {
    return {
      valid: false,
      error: `${label}: negative quantity cannot exceed the item recipe amount (${itemLine.quantity}).`,
    };
  }
  return { valid: true };
}

function Recipes({
  value = [],
  onChange,
  disabled = false,
  withSection = true,
  allowNonPositive = false,
  itemRecipeLines = [],
  description = 'Link ingredients and quantities to this item.',
}) {
  const { organizationId } = useContext(AdminContext);
  const { message } = App.useApp();
  const [selectedIngredientId, setSelectedIngredientId] = useState();

  const ingredientsParams = organizationId ? {
    url: `v1/organizations/${organizationId}/ingredients/`,
    query: { page_size: 500 },
  } : null;

  const {
    data: ingredients = [],
    loading,
    error,
  } = useFetchedData(
    ingredientsParams,
    normalizeIngredientsResponse,
  );

  const ingredientById = useMemo(
    () => ingredients.reduce((acc, ingredient) => ({ ...acc, [ingredient.id]: ingredient }), {}),
    [ingredients],
  );

  const rows = useMemo(() => (
    (value || []).map((line, index) => {
      const ingredient = ingredientById[line.ingredient] || {};
      return {
        key: line.id || `${line.ingredient}-${index}`,
        ingredient: line.ingredient,
        ingredient_name: line.ingredient_name || ingredient.name,
        ingredient_unit: line.ingredient_unit || ingredient.unit,
        ingredient_category: line.ingredient_category || ingredient.category,
        quantity: line.quantity,
      };
    })
  ), [ingredientById, value]);

  const setLines = (nextLines) => {
    onChange?.(nextLines);
  };

  const addIngredient = () => {
    if (!selectedIngredientId) {
      return;
    }
    if (rows.some((line) => line.ingredient === selectedIngredientId)) {
      message.warning('Ingredient already added to this recipe.');
      return;
    }

    const ingredient = ingredientById[selectedIngredientId];
    if (!ingredient) {
      return;
    }

    setLines([
      ...rows,
      {
        ingredient: ingredient.id,
        ingredient_name: ingredient.name,
        ingredient_unit: ingredient.unit,
        ingredient_category: ingredient.category,
        quantity: 1,
      },
    ]);
    setSelectedIngredientId(undefined);
  };

  const updateQuantity = (ingredientId, quantity) => {
    if (quantity != null) {
      if (allowNonPositive && Number(quantity) === 0) {
        message.warning('Quantity must not be zero.');
        return;
      }
      if (!allowNonPositive && quantity <= 0) {
        message.warning('Quantity must be greater than zero.');
        return;
      }
      if (allowNonPositive && itemRecipeLines?.length) {
        const negativeCheck = validateNegativeModifierQuantity(ingredientId, quantity, itemRecipeLines);
        if (!negativeCheck.valid) {
          message.error(negativeCheck.error);
        }
      }
    }
    setLines(rows.map((line) => (
      line.ingredient === ingredientId
        ? { ...line, quantity }
        : line
    )));
  };

  const removeLine = (ingredientId) => {
    setLines(rows.filter((line) => line.ingredient !== ingredientId));
  };

  const columns = [
    {
      title: 'Ingredient',
      dataIndex: 'ingredient_name',
      key: 'ingredient_name',
      render: (name, record) => (
        <Typography.Text strong>{name || `#${record.ingredient}`}</Typography.Text>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 150,
      render: (lineQuantity, record) => (
        <InputNumber
          step={0.01}
          precision={2}
          controls={false}
          value={lineQuantity}
          disabled={disabled}
          style={{ width: '100%' }}
          onChange={(nextValue) => updateQuantity(record.ingredient, nextValue)}
        />
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'ingredient_unit',
      key: 'ingredient_unit',
      width: 90,
      render: (unit) => unit || '-',
    },
    !disabled && {
      title: '',
      key: 'actions',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removeLine(record.ingredient)}
        />
      ),
    },
  ].filter(Boolean);

  const content = (
    <>
      <Typography.Text strong>Recipes</Typography.Text>
      {description ? (
        <Typography.Paragraph type="secondary" className="mb-2">
          {description}
        </Typography.Paragraph>
      ) : null}

      {error && (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message="Unable to load ingredients for this organization."
        />
      )}

      {!disabled && (
        <Row gutter={12} className="mb-3">
          <Col span={18}>
            <Select
              showSearch
              allowClear
              loading={loading}
              disabled={!organizationId || !!error}
              className="w-full"
              placeholder="Search ingredient to add"
              options={ingredients}
              value={selectedIngredientId}
              optionFilterProp="label"
              onChange={setSelectedIngredientId}
            />
          </Col>
          <Col span={6}>
            <Button
              icon={<PlusOutlined />}
              block
              onClick={addIngredient}
              disabled={!selectedIngredientId}
            >
              Add
            </Button>
          </Col>
        </Row>
      )}

      <Table
        columns={columns}
        dataSource={rows}
        style={{ width: '100%' }}
        pagination={false}
        locale={{ emptyText: 'No recipe ingredients added yet.' }}
        size="small"
        scroll={{ x: 480 }}
      />
    </>
  );

  if (!withSection) {
    return content;
  }

  return (
    <FormLayout.Section>
      {content}
    </FormLayout.Section>
  );
}

export default Recipes;
